<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },
  headerTitle: {
    type: String,
    default: '',
  },
  showHeaderAction: {
    type: Boolean,
    default: false,
  },
  headerActionLabel: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['mentionSelect', 'headerAction']);

const mentionsListContainerRef = ref(null);
const mentionScrollContainerRef = ref(null);
const selectedIndex = ref(0);
const mentionPanelMaxHeight = 'min(calc(100vh - 5rem), 48rem)';
const hasHeader = computed(() => props.headerTitle || props.showHeaderAction);
const listMaxHeight = computed(() =>
  hasHeader.value
    ? `calc(${mentionPanelMaxHeight} - 3.75rem)`
    : mentionPanelMaxHeight
);

const adjustScroll = () => {
  nextTick(() => {
    const container = mentionScrollContainerRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#mention-item-${selectedIndex.value}`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  emit('mentionSelect', props.items[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => props.items),
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => props.items,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
  }
);

watch(selectedIndex, adjustScroll);

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const onHeaderActionClick = () => {
  emit('headerAction');
};

const onListWheel = event => {
  const container = mentionScrollContainerRef.value;
  if (!container) return;

  container.scrollTop += event.deltaY;
  event.preventDefault();
  event.stopPropagation();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div
    ref="mentionsListContainerRef"
    class="bg-n-solid-1 p-1 rounded-xl overflow-hidden absolute w-full z-20 shadow-md left-0 bottom-full border border-solid border-n-strong mention--box"
  >
    <div
      v-if="hasHeader"
      class="mention--box__header flex items-center justify-between gap-3 px-3 py-2 mb-1"
    >
      <p class="mb-0 text-sm font-medium text-n-slate-11">
        {{ headerTitle }}
      </p>
      <button
        v-if="showHeaderAction"
        type="button"
        class="mention--box__header-action inline-flex items-center gap-2 min-h-9 px-3 rounded-lg text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12 transition-colors"
        :aria-label="headerActionLabel"
        :title="headerActionLabel"
        @mousedown.prevent.stop
        @click.stop="onHeaderActionClick"
      >
        <fluent-icon icon="edit" class="size-4 flex-shrink-0" />
        <span class="text-xs font-medium whitespace-nowrap">
          {{ headerActionLabel }}
        </span>
      </button>
    </div>
    <div
      ref="mentionScrollContainerRef"
      class="mention--box__list overflow-y-scroll pr-1"
      :style="{ maxHeight: listMaxHeight }"
      @wheel.prevent.stop="onListWheel"
      @touchmove.stop
    >
      <ul class="mb-0 vertical dropdown menu">
        <woot-dropdown-item
          v-for="(item, index) in items"
          :id="`mention-item-${index}`"
          :key="item.key"
          class="!mb-1"
          @mouseover="onHover(index)"
        >
          <button
            class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
            :class="{
              'bg-n-alpha-black2': index === selectedIndex,
            }"
            @click="onListItemSelection(index)"
          >
            <slot :item="item" :index="index" :selected="index === selectedIndex">
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ item.description }}
              </p>
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ variableKey(item) }}
              </p>
            </slot>
          </button>
        </woot-dropdown-item>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  @apply flex flex-col;

  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.mention--box__header {
  border-bottom: 1px solid rgb(var(--border-weak) / 1);
  @apply flex-shrink-0;
}

.mention--box__list {
  @apply min-h-0;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
